/**
 * Sprint BG-MasterIO — M2 aiCategoryMasters の rows[] 専用 CSV I/O。
 *
 * 用途:
 *   - 地区間で「分類行のみ」を Excel / スプレッドシート経由でレビュー → 反映
 *   - 大量行 (50+) を CSV で一括編集 (Excel paste / column drag fill)
 *
 * 対象 columns:
 *   - 必須 (core) 列 — マスタの JSON フォーマットに依らず常に列として往復する:
 *     `categoryCd, category, majorCd, major, color, example, definition, criteria,
 *      excludeFromAggregation`
 *     (example / definition / criteria は AI 分類精度を左右する必須情報のため、値が空でも列自体は必ず出力する)
 *   - optional 列 — 上記以外に rows[] へ実際に現れているキー (例: gqlCodes, chainRules, icon、
 *     あるいは将来追加される任意のマスタ固有フィールド) は、マスタの JSON フォーマットごとに
 *     異なりうるため列名をコード側にハードコードしない。実データ (または CSV header) から
 *     動的に検出し、そのままの列名で往復させる。値は文字列ならそのまま、それ以外は
 *     JSON.stringify / JSON.parse でラウンドトリップする。
 *
 * RFC4180:
 *   - 値が `,` / `"` / `\n` を含む場合は `"..."` で囲み、内部 `"` は `""` にエスケープ
 *   - 改行は `\r\n` (Excel 互換)
 *   - header 必須 (parse 時は header 行で順序を決定、欠落 column は空文字 fallback)
 */

import type { AiCategoryMasterRow } from '@citizen-walk/shared-types';
import { isSafeCssColor } from '@citizen-walk/shared-ui';

/** マスタの JSON フォーマットに依らず常に列として往復する core column (この順序で出力)。 */
export const CORE_ROW_COLUMNS = [
  'categoryCd',
  'category',
  'majorCd',
  'major',
  'color',
  'example',
  'definition',
  'criteria',
  'excludeFromAggregation',
] as const;
type CoreRowColumn = (typeof CORE_ROW_COLUMNS)[number];

/** 指定 key が core column (常設列) かどうか。 */
export function isCoreRowColumn(key: string): key is CoreRowColumn {
  return (CORE_ROW_COLUMNS as readonly string[]).includes(key);
}

/**
 * rows[] に実際に現れる core 以外のキーを初出順に収集する。
 * gqlCodes / chainRules 等の optional 列はマスタ (JSON フォーマット) ごとに異なるため、
 * 列名をハードコードせず実データから動的に導出する。
 */
export function collectOptionalColumnKeys(rows: AiCategoryMasterRow[]): string[] {
  const seen: string[] = [];
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (isCoreRowColumn(key)) continue;
      if (!seen.includes(key)) seen.push(key);
    }
  }
  return seen;
}

/** optional 列の値をセル文字列へ変換する (文字列はそのまま、それ以外は JSON 化)。 */
export function formatOptionalCellValue(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

/** セル文字列を optional 列の値へ復元する (JSON として解釈できればそれを、できなければ素の文字列)。 */
export function parseOptionalCellValue(raw: string): unknown {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  try {
    return JSON.parse(trimmed);
  } catch {
    return trimmed;
  }
}

/** 1 セルを RFC4180 風に quote する。 */
function quoteCell(value: string): string {
  if (value === '') return '';
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * AiCategoryMasterRow[] を CSV 文字列にシリアライズ。
 * `optionalColumns` を省略すると rows[] から動的に検出する。呼び出し側で列の存在・順序を
 * 固定したい場合 (例: 値が空の optional 列も出力したい場合) は明示的に渡す。
 */
export function serializeCategoryRowsToCsv(
  rows: AiCategoryMasterRow[],
  optionalColumns: string[] = collectOptionalColumnKeys(rows),
): string {
  const columns: string[] = [...CORE_ROW_COLUMNS, ...optionalColumns];
  const lines = [columns.map(quoteCell).join(',')];
  for (const row of rows) {
    const r = row as unknown as Record<string, unknown>;
    const cells = columns.map((col) => {
      if (col === 'excludeFromAggregation') {
        return quoteCell(row.excludeFromAggregation ? 'true' : '');
      }
      return quoteCell(formatOptionalCellValue(r[col]));
    });
    lines.push(cells.join(','));
  }
  // 末尾改行で Excel 等の挙動を安定化
  return lines.join('\r\n') + '\r\n';
}

/**
 * 1 行 (header または data) を RFC4180 風に parse。
 * "..." 内の `""` は `"`、quoted 値内の `,` / 改行はリテラルとして保持。
 */
function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else {
      if (ch === ',') {
        cells.push(cur);
        cur = '';
      } else if (ch === '"' && cur === '') {
        inQuotes = true;
      } else {
        cur += ch;
      }
    }
  }
  cells.push(cur);
  return cells;
}

export interface CsvParseResult {
  rows: AiCategoryMasterRow[];
  /**
   * header に含まれていた core 以外の列 (= optional 列)、初出順。
   * 呼び出し側 (admin UI) が「今回の CSV にどんな optional 列があったか」を反映する際に使う。
   */
  optionalColumns: string[];
  errors: string[];
}

/**
 * CSV 文字列を AiCategoryMasterRow[] に parse。
 *
 * 検証:
 *   - header 行が存在し、最低限 categoryCd / category column を含む
 *   - 各 data 行: categoryCd / category 必須 (どちらか空ならその行は skip + errors 追加)
 *
 * header の core 以外の列は optional 列としてそのまま採用する (列名をハードコードしない)。
 * CRLF / LF / CR どれでも改行として処理。
 */
export function parseCategoryRowsFromCsv(text: string): CsvParseResult {
  const errors: string[] = [];
  // BOM 除去 + 改行統一
  const normalized = text.replace(/^\uFEFF/, '').replace(/\r\n?|\n/g, '\n');
  // quoted 内の改行を安全に拾うため、まず文字列を行に分割しつつ quote state を見る
  const lines: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < normalized.length; i += 1) {
    const ch = normalized[i];
    if (ch === '"') {
      // 連続 "" は escape
      if (inQuotes && normalized[i + 1] === '"') {
        cur += '""';
        i += 1;
      } else {
        inQuotes = !inQuotes;
        cur += ch;
      }
    } else if (ch === '\n' && !inQuotes) {
      lines.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  if (cur.length > 0) lines.push(cur);

  if (lines.length === 0) {
    return { rows: [], optionalColumns: [], errors: ['CSV が空です'] };
  }

  const headerCells = parseCsvLine(lines[0]!).map((c) => c.trim());
  const categoryCdIdx = headerCells.indexOf('categoryCd');
  const categoryIdx = headerCells.indexOf('category');
  if (categoryCdIdx < 0 || categoryIdx < 0) {
    return {
      rows: [],
      optionalColumns: [],
      errors: [
        `header に必須 column が不足しています (categoryCd, category)。検出: ${headerCells.join(',')}`,
      ],
    };
  }

  // core 以外の header 列はマスタ (JSON フォーマット) 依存の optional 列としてそのまま採用。
  const optionalColumns = headerCells.filter((h) => h !== '' && !isCoreRowColumn(h));

  const get = (cells: string[], colName: string): string => {
    const idx = headerCells.indexOf(colName);
    if (idx < 0) return '';
    return (cells[idx] ?? '').trim();
  };

  const rows: AiCategoryMasterRow[] = [];
  for (let i = 1; i < lines.length; i += 1) {
    const raw = lines[i]!;
    if (raw.trim() === '') continue;
    const cells = parseCsvLine(raw);
    const categoryCd = get(cells, 'categoryCd');
    const category = get(cells, 'category');
    if (!categoryCd || !category) {
      errors.push(`行 ${i + 1}: categoryCd / category が必須です (skip)`);
      continue;
    }
    const row: Record<string, unknown> = { categoryCd, category };
    for (const col of ['majorCd', 'major', 'color', 'example', 'definition', 'criteria'] as const) {
      const v = get(cells, col);
      if (!v) continue;
      // 色は地図の SVG / style に埋め込まれる。色として解釈できない値は取り込まない (docs/13 §7)。
      if (col === 'color' && !isSafeCssColor(v)) {
        errors.push(
          `行 ${i + 1}: 色の値が正しくないため無視しました (#RRGGBB の形式で指定してください)`,
        );
        continue;
      }
      row[col] = v;
    }
    const excludeFlag = get(cells, 'excludeFromAggregation');
    if (excludeFlag) row.excludeFromAggregation = /^(true|1|yes)$/i.test(excludeFlag);
    for (const col of optionalColumns) {
      const v = get(cells, col);
      if (v === '') continue;
      row[col] = parseOptionalCellValue(v);
    }
    rows.push(row as unknown as AiCategoryMasterRow);
  }

  return { rows, optionalColumns, errors };
}
